/**
 * Checklists Iniciales de Exocortex
 *
 * Escenarios predefinidos basados en las necesidades del usuario:
 * - Items criticos claramente marcados (salbutamol, pastillas, carnet, llaves, billetera)
 * - Categorias para organizacion visual
 * - Diseñado para reducir carga cognitiva en ADHD/TEA
 * - Terminologia chilena (carnet, billetera, celular)
 */

import type { Checklist } from '@/core/types';

const now = new Date().toISOString();

export const DEFAULT_CHECKLISTS: Checklist[] = [
  {
    id: 'chk-work',
    name: 'Trabajo',
    description: 'Ir a la oficina',
    emoji: '💼',
    order: 0,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-w-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },
      {
        id: 'item-w-pills',
        label: 'Pastillas del dia',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 1,
      },

      // Personal - Esenciales
      {
        id: 'item-w-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 2,
      },
      {
        id: 'item-w-wallet',
        label: 'Billetera',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 3,
      },
      {
        id: 'item-w-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 4,
      },
      {
        id: 'item-w-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 5,
      },

      // Work - Equipo de trabajo
      {
        id: 'item-w-macbook',
        label: 'MacBook',
        isChecked: false,
        priority: 'critical',
        category: 'work',
        order: 6,
      },
      {
        id: 'item-w-mac-charger',
        label: 'Cargador MacBook',
        isChecked: false,
        priority: 'critical',
        category: 'work',
        order: 7,
      },
      {
        id: 'item-w-mouse',
        label: 'Mouse',
        isChecked: false,
        priority: 'normal',
        category: 'work',
        order: 8,
      },
      {
        id: 'item-w-keyboard',
        label: 'Teclado',
        isChecked: false,
        priority: 'normal',
        category: 'work',
        order: 9,
      },

      // Tech
      {
        id: 'item-w-headphones',
        label: 'Audifonos',
        isChecked: false,
        priority: 'normal',
        category: 'tech',
        order: 10,
      },
      {
        id: 'item-w-charger',
        label: 'Cargador celular',
        isChecked: false,
        priority: 'normal',
        category: 'tech',
        order: 11,
      },

      // Hygiene
      {
        id: 'item-w-handcream',
        label: 'Crema de manos',
        isChecked: false,
        priority: 'normal',
        category: 'hygiene',
        order: 12,
      },
    ],
  },

  {
    id: 'chk-casual',
    name: 'Salir',
    description: 'Planes, paseo',
    emoji: '🚶',
    order: 1,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-c-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },

      // Personal - Esenciales
      {
        id: 'item-c-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 1,
      },
      {
        id: 'item-c-wallet',
        label: 'Billetera',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 2,
      },
      {
        id: 'item-c-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 3,
      },
      {
        id: 'item-c-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 4,
      },

      // Hygiene
      {
        id: 'item-c-handcream',
        label: 'Crema de manos',
        isChecked: false,
        priority: 'normal',
        category: 'hygiene',
        order: 5,
      },
    ],
  },

  {
    id: 'chk-shopping',
    name: 'Compras',
    description: 'Super, feria, tiendas',
    emoji: '🛒',
    order: 2,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-sh-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },

      // Personal - Esenciales
      {
        id: 'item-sh-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 1,
      },
      {
        id: 'item-sh-wallet',
        label: 'Billetera',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 2,
      },
      {
        id: 'item-sh-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 3,
      },
      {
        id: 'item-sh-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 4,
      },

      // Other - Compras
      {
        id: 'item-sh-bags',
        label: 'Bolsas reutilizables',
        isChecked: false,
        priority: 'normal',
        category: 'other',
        order: 5,
      },
      {
        id: 'item-sh-list',
        label: 'Lista de compras',
        isChecked: false,
        priority: 'normal',
        category: 'other',
        order: 6,
        note: 'Revisar que llevas la lista',
      },
    ],
  },

  {
    id: 'chk-overnight',
    name: 'Quedarme fuera',
    description: 'Noche fuera de casa',
    emoji: '🌙',
    order: 3,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-o-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },
      {
        id: 'item-o-pills',
        label: 'Pastillas del dia',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 1,
      },
      {
        id: 'item-o-pills-next',
        label: 'Pastillas del dia siguiente',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 2,
        note: 'No olvidar!',
      },

      // Personal - Esenciales
      {
        id: 'item-o-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 3,
      },
      {
        id: 'item-o-wallet',
        label: 'Billetera',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 4,
      },
      {
        id: 'item-o-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 5,
      },
      {
        id: 'item-o-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 6,
      },

      // Tech
      {
        id: 'item-o-charger',
        label: 'Cargador celular',
        isChecked: false,
        priority: 'normal',
        category: 'tech',
        order: 7,
      },

      // Hygiene
      {
        id: 'item-o-handcream',
        label: 'Crema de manos',
        isChecked: false,
        priority: 'normal',
        category: 'hygiene',
        order: 8,
      },
      {
        id: 'item-o-toothbrush',
        label: 'Cepillo de dientes',
        isChecked: false,
        priority: 'normal',
        category: 'hygiene',
        order: 9,
      },

      // Clothing
      {
        id: 'item-o-clothes',
        label: 'Muda de ropa',
        isChecked: false,
        priority: 'normal',
        category: 'clothing',
        order: 10,
      },
    ],
  },

  {
    id: 'chk-gym',
    name: 'Gimnasio',
    description: 'Ir al gym',
    emoji: '🏋️',
    order: 4,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-g-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },

      // Personal - Esenciales
      {
        id: 'item-g-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 1,
      },
      {
        id: 'item-g-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 2,
      },
      {
        id: 'item-g-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 3,
      },

      // Tech
      {
        id: 'item-g-headphones',
        label: 'Audifonos',
        isChecked: false,
        priority: 'normal',
        category: 'tech',
        order: 4,
      },

      // Clothing
      {
        id: 'item-g-clothes',
        label: 'Ropa deportiva',
        isChecked: false,
        priority: 'normal',
        category: 'clothing',
        order: 5,
      },
      {
        id: 'item-g-sneakers',
        label: 'Zapatillas',
        isChecked: false,
        priority: 'normal',
        category: 'clothing',
        order: 6,
      },

      // Hygiene
      {
        id: 'item-g-towel',
        label: 'Toalla',
        isChecked: false,
        priority: 'normal',
        category: 'hygiene',
        order: 7,
      },
    ],
  },

  {
    id: 'chk-medical',
    name: 'Cita medica',
    description: 'Medico, dentista, especialista',
    emoji: '🏥',
    order: 5,
    isDefault: true,
    createdAt: now,
    updatedAt: now,
    items: [
      // Critical - Health
      {
        id: 'item-m-salbutamol',
        label: 'Salbutamol',
        isChecked: false,
        priority: 'critical',
        category: 'health',
        order: 0,
      },

      // Personal - Documentos importantes
      {
        id: 'item-m-carnet',
        label: 'Carnet',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 1,
      },
      {
        id: 'item-m-fonasa',
        label: 'Credencial Fonasa/Isapre',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 2,
      },
      {
        id: 'item-m-wallet',
        label: 'Billetera',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 3,
      },
      {
        id: 'item-m-keys',
        label: 'Llaves de casa',
        isChecked: false,
        priority: 'critical',
        category: 'personal',
        order: 4,
      },
      {
        id: 'item-m-phone',
        label: 'Celular',
        isChecked: false,
        priority: 'normal',
        category: 'personal',
        order: 5,
      },

      // Other - Documentos medicos
      {
        id: 'item-m-reports',
        label: 'Examenes / recetas previas',
        isChecked: false,
        priority: 'normal',
        category: 'other',
        order: 6,
        note: 'Si aplica',
      },
      {
        id: 'item-m-questions',
        label: 'Lista de preguntas / sintomas',
        isChecked: false,
        priority: 'normal',
        category: 'other',
        order: 7,
      },
    ],
  },
];
